// src/aws/S3Upload.ts
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { HeadObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { AbortController } from "@aws-sdk/abort-controller";
import { AdaptiveRetryStrategy } from "@aws-sdk/util-retry";
import type { IS3Config } from "../redux/types/upload.types";
import { getAwsClients } from "./awsClients";

export class S3Upload {
	// Kept for compatibility with your current class shape
	cognitoClient: CognitoIdentityClient;
	requestHandler: XhrHttpHandler;
	client: S3Client;

	s3Config: IS3Config;
	abortController: AbortController;
	parallelUploads3: Upload;

	fetchFileSize: (Bucket: string, Key: string) => Promise<number | undefined>;

	// Kept for compatibility, but now sourced from global bundle
	retryStrategy: AdaptiveRetryStrategy;

	readonly maxAttempts = 20;
	readonly retryMode = "adaptive";
	readonly queueSize = 6;
	readonly partSize = 1024 * 1024 * 5; // 5mb

	constructor(s3Config: IS3Config, file: any) {
		// Get global/shared clients (memoized)
		const bundle = getAwsClients(s3Config);

		this.cognitoClient = bundle.cognitoClient;
		this.requestHandler = bundle.requestHandler;
		this.client = bundle.s3Client;
		this.retryStrategy = bundle.retryStrategy;

		this.s3Config = s3Config;
		this.abortController = new AbortController();

		// Upload instance remains per-file/per-upload
		this.parallelUploads3 = new Upload({
			client: this.client,
			params: {
				Bucket: this.s3Config.bucket,
				Key: file.key,
				Body: file.file,
			},
			queueSize: this.queueSize,
			partSize: this.partSize,
			leavePartsOnError: false,
		});

		this.fetchFileSize = async (
			Bucket: string,
			Key: string
		): Promise<number | undefined> => {
			try {
				const head = await this.client.send(
					new HeadObjectCommand({ Bucket, Key }),
					// Optional: allow aborting this request via abortController
					{ abortSignal: this.abortController.signal }
				);
				return head.ContentLength;
			} catch (err) {
				throw err;
			}
		};
	}

	/**
	 * Optional convenience method if you want to abort the multipart upload
	 */
	abortUpload() {
		this.parallelUploads3?.abort();
		// Also abort any command-based requests (e.g., HeadObject)
		this.abortController?.abort();
	}
}
